import React from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { format, isValid } from 'date-fns';

const { FiX, FiUser, FiPhone, FiMapPin, FiAward, FiBriefcase, FiSmartphone, FiShield, FiFlag, FiCreditCard, FiGift } = FiIcons;

const DetailItem = ({ icon, label, value, subValue, highlight = false, statusVariant = false }) => {
  const getStatusColor = (val) => {
    if (val === 'Confirmed' || val === '1' || val === 'Yes') return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (val === 'Pending') return 'text-amber-600 bg-amber-50 border-amber-100';
    if (val === 'No' || val === '0') return 'text-red-600 bg-red-50 border-red-100';
    if (val === 'Not Required') return 'text-surface-500 bg-surface-50 border-surface-200';
    return 'text-surface-900';
  };

  return (
    <div className="flex items-start gap-4 p-4 rounded-2xl hover:bg-surface-50 transition-colors border border-transparent hover:border-surface-100">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${highlight ? 'bg-primary-100 text-primary-600' : 'bg-surface-100 text-surface-400'}`}>
        <SafeIcon icon={icon} className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black text-surface-400 uppercase tracking-widest mb-0.5">{label}</p>
        <p className={`text-sm font-bold truncate ${highlight ? 'text-primary-700' : 'text-surface-900'} ${statusVariant ? 'px-2 py-0.5 rounded-lg border text-[11px] uppercase tracking-tighter inline-block ' + getStatusColor(value) : ''}`}>
          {value || '—'}
        </p>
        {subValue && <p className="text-[10px] font-bold text-surface-400 mt-0.5 leading-relaxed">{subValue}</p>}
      </div>
    </div>
  );
};

const GiftOptions = ({ gifts = [], selectedGift }) => (
  <div className="bg-primary-50/50 rounded-[2rem] p-6 border border-primary-100">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white">
        <SafeIcon icon={FiAward} className="w-5 h-5" />
      </div>
      <h4 className="text-sm font-black text-primary-900 uppercase">Rewards Status</h4>
    </div>
    
    {selectedGift ? (
      <div className="bg-white p-4 rounded-2xl border border-primary-200 shadow-sm">
        <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest mb-1">Final Selection</p>
        <p className="text-lg font-black text-surface-900">{selectedGift}</p>
      </div>
    ) : (
      <div className="space-y-2">
        <p className="text-[10px] font-black text-surface-400 uppercase tracking-widest mb-2">Available Gift Options</p>
        {gifts.length > 0 ? gifts.map((g, i) => (
          <div key={i} className="bg-white/60 p-3 rounded-xl border border-primary-100 text-sm font-bold text-surface-600 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiGift} className="w-3.5 h-3.5 text-primary-400 shrink-0" />
              {g.gift_name}
            </div>
            {g.is_selected === "1" && <span className="text-[9px] font-black text-emerald-600 uppercase">Selected</span>}
          </div>
        )) : <p className="text-xs text-surface-400 italic">No gift options assigned.</p>}
      </div>
    )}
  </div>
);

const UserDetailsModal = ({ mobileNo, onClose, schemeId }) => {
  const { data: responseData, isLoading, isError } = useQuery({
    queryKey: ['user_details', mobileNo, schemeId],
    queryFn: async () => {
      // Updated to POST to match your new user_details.php logic
      const res = await api.post('user_details.php', {
        mobile_no: mobileNo,
        scheme_id: schemeId
      });
      return res.data?.data;
    },
    enabled: !!mobileNo && !!schemeId,
  });

  const user = responseData?.user;
  const gifts = responseData?.gifts || [];

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isValid(date) ? format(date, 'PPP p') : d;
  };

  const getOwnerStatus = (s) => {
    if (!s && s !== 0) return 'Pending';
    if (s === '1' || s === 'Yes') return 'Confirmed';
    if (s === '0' || s === 'No') return 'No';
    return s;
  };

  const getPanStatus = (pan) => {
    if (!pan || pan.trim() === '' || pan.toLowerCase() === 'null') return 'Pending';
    return pan;
  };

  const modalContent = (
    <AnimatePresence>
      {mobileNo && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[999] bg-surface-950/40 backdrop-blur-sm"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-y-0 right-0 z-[1000] w-full max-w-lg bg-white shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary-600 flex items-center justify-center text-white shadow-lg shadow-primary-200">
                  <SafeIcon icon={FiUser} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-surface-900 leading-none">User Profile</h3>
                  <p className="text-xs text-surface-400 font-bold mt-1 uppercase tracking-wider">ID: {mobileNo}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2.5 hover:bg-surface-100 rounded-xl transition-colors">
                <SafeIcon icon={FiX} className="w-5 h-5 text-surface-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-40 w-full rounded-3xl" />
                  <Skeleton className="h-40 w-full rounded-3xl" />
                </div>
              ) : isError ? (
                <div className="py-20 text-center">
                  <p className="text-surface-900 font-black uppercase text-sm">Profile Load Failed</p>
                  <Button variant="outline" className="mt-4 rounded-xl" onClick={onClose}>Close</Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Business Info</h4>
                    <DetailItem 
                      icon={FiBriefcase} 
                      label="Outlet Name" 
                      value={user?.updated_outlet_name || user?.outlet_name} 
                      subValue={user?.updated_outlet_name ? `Original: ${user?.outlet_name}` : null}
                      highlight={!!user?.updated_outlet_name}
                    />
                    <DetailItem icon={FiShield} label="Shop Owner" value={getOwnerStatus(user?.shop_owner)} statusVariant />
                    <DetailItem icon={FiCreditCard} label="PAN Card" value={getPanStatus(user?.pan_card)} statusVariant={['Pending', 'Not Required'].includes(getPanStatus(user?.pan_card))} />
                    <DetailItem icon={FiAward} label="Achieved Slab" value={user?.achieved_slab} />
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Contact</h4>
                    <DetailItem icon={FiPhone} label="Primary Number" value={user?.mobile_no} />
                    <DetailItem icon={FiSmartphone} label="Alternate Number" value={user?.alternate_mobile_no} />
                    <DetailItem icon={FiFlag} label="Language / Preferred" value={`${user?.language || '—'} / ${user?.preferred_language || '—'}`} />
                  </div>

                  <GiftOptions gifts={gifts} selectedGift={user?.selected_gift} />

                  <div className="space-y-2">
                    <h4 className="text-[10px] font-black text-primary-600 uppercase tracking-[0.2em] ml-1">Address</h4>
                    <DetailItem 
                      icon={FiMapPin} 
                      label="Shop / Area" 
                      value={`${user?.shop_no || ''}, ${user?.area_name || ''}`} 
                      subValue={user?.selected_landmark ? `Landmark: ${user?.selected_landmark}` : null}
                    />
                    <DetailItem 
                      icon={FiMapPin} 
                      label="City / State" 
                      value={`${user?.city || '—'}, ${user?.state || '—'}`} 
                      subValue={`Pincode: ${user?.pincode || '—'}`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-surface-100">
                    <div>
                      <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest mb-1">Enrolled On</p>
                      <p className="text-[11px] font-bold text-surface-700">{formatDate(user?.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-surface-400 uppercase tracking-widest mb-1">Last Update</p>
                      <p className="text-[11px] font-bold text-surface-700">{formatDate(user?.updated_at)}</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="p-6 bg-surface-50 border-t border-surface-100">
              <Button onClick={onClose} className="w-full h-12 rounded-2xl bg-surface-900 hover:bg-black font-black uppercase text-xs tracking-widest">Close Profile</Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default UserDetailsModal;